import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Plus, Trash2, ChevronUp, ChevronDown, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { invalidateProductsCache } from '../../services/products';

const PRODUCT_CLASSES = [
  { id: 'complete-scooters', name: 'Tõuksid' },
  { id: 'decks', name: 'Tallad' },
  { id: 'bars', name: 'Lenksud' },
  { id: 'wheels', name: 'Rattad' },
  { id: 'forks', name: 'Kahvlid' },
  { id: 'clamps', name: 'Klambrid' },
  { id: 'bar-ends', name: 'Lenksuotsad' },
  { id: 'pegs', name: 'Peegid' },
  { id: 'grips', name: 'Käepidemed' },
  { id: 'griptape', name: 'Gripteibid' },
];

interface SpecRow { label: string; value: string; }
interface Spec {
  title: string;
  description?: string;
  table?: { main: SpecRow[] };
}

interface Props {
  product: any;
  onClose: () => void;
  onSaved: () => void;
}

// ─── Auto-growing textarea ────────────────────────────────────────────────────
function AutoTextarea({ value, onChange, placeholder, minRows = 2 }: {
  value: string; onChange: (v: string) => void; placeholder?: string; minRows?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = ref.current.scrollHeight + 24 + 'px';
    }
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={minRows}
      className="w-full bg-white border border-gray-300 text-gray-900 text-sm px-3 py-2.5 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent transition overflow-hidden"
      style={{ minHeight: `${minRows * 36}px` }}
    />
  );
}

// ─── Chip Input ───────────────────────────────────────────────────────────────
function ChipInput({ label, chips, setChips }: {
  label: string; chips: string[]; setChips: (c: string[]) => void;
}) {
  const [input, setInput] = useState('');
  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault();
      if (!chips.includes(input.trim())) setChips([...chips, input.trim()]);
      setInput('');
    } else if (e.key === 'Backspace' && !input && chips.length > 0) {
      setChips(chips.slice(0, -1));
    }
  };
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1.5">{label}</label>
      <div className="bg-white border border-gray-300 rounded-xl p-2 flex flex-wrap gap-1.5 min-h-[44px]">
        {chips.map(chip => (
          <span key={chip} className="inline-flex items-center gap-1 bg-gray-100 text-gray-800 text-xs px-2.5 py-1 rounded-full">
            {chip}
            <button onClick={() => setChips(chips.filter(c => c !== chip))} className="text-gray-400 hover:text-red-500 transition">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Lisa, vajuta Enter"
          className="flex-1 min-w-[120px] bg-transparent text-gray-900 text-sm outline-none placeholder-gray-400 px-1"
        />
      </div>
    </div>
  );
}

// ─── Specifications Editor ────────────────────────────────────────────────────
function SpecsEditor({ specs, setSpecs }: { specs: Spec[]; setSpecs: (s: Spec[]) => void }) {
  const updateSpec = (i: number, updates: Partial<Spec>) =>
    setSpecs(specs.map((s, idx) => (idx === i ? { ...s, ...updates } : s)));

  const toggleType = (i: number) => {
    const s = specs[i];
    if (s.table) {
      const { table, ...rest } = s;
      updateSpec(i, { ...rest, description: '' });
    } else {
      const { description, ...rest } = s;
      updateSpec(i, { ...rest, table: { main: [{ label: '', value: '' }] } });
    }
  };

  const updateRow = (specIdx: number, rowIdx: number, field: 'label' | 'value', val: string) => {
    const s = specs[specIdx];
    if (!s.table) return;
    const main = s.table.main.map((r, j) => (j === rowIdx ? { ...r, [field]: val } : r));
    updateSpec(specIdx, { table: { main } });
  };

  return (
    <div className="space-y-4">
      {specs.map((s, i) => (
        <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <input
              value={s.title}
              onChange={e => updateSpec(i, { title: e.target.value })}
              className="flex-1 bg-white border border-gray-300 text-gray-900 text-sm font-bold px-3 py-1.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4FC1E9]"
              placeholder="Pealkiri (nt TEHNILISED ANDMED)"
            />
            <button
              onClick={() => toggleType(i)}
              className="text-xs text-gray-500 hover:text-[#4FC1E9] border border-gray-300 px-2.5 py-1.5 rounded-lg transition whitespace-nowrap"
            >
              {s.table ? 'Tabel ✓' : 'Tekst ✓'}
            </button>
            <button onClick={() => setSpecs(specs.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 transition">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {s.table ? (
            <div className="space-y-2">
              {s.table.main.map((row, j) => (
                <div key={j} className="flex gap-2 items-center">
                  <input
                    value={row.label}
                    onChange={e => updateRow(i, j, 'label', e.target.value)}
                    placeholder="Atribuut"
                    className="w-2/5 bg-white border border-gray-300 text-gray-900 text-xs px-3 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#4FC1E9]"
                  />
                  <input
                    value={row.value}
                    onChange={e => updateRow(i, j, 'value', e.target.value)}
                    placeholder="Väärtus"
                    className="flex-1 bg-white border border-gray-300 text-gray-900 text-xs px-3 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#4FC1E9]"
                  />
                  <button
                    onClick={() => {
                      const main = s.table!.main.filter((_, k) => k !== j);
                      updateSpec(i, { table: { main } });
                    }}
                    className="text-gray-400 hover:text-red-500 transition flex-shrink-0"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const main = [...(s.table?.main || []), { label: '', value: '' }];
                  updateSpec(i, { table: { main } });
                }}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#4FC1E9] transition mt-1"
              >
                <Plus className="w-3.5 h-3.5" /> Lisa rida
              </button>
            </div>
          ) : (
            <AutoTextarea
              value={s.description || ''}
              onChange={v => updateSpec(i, { description: v })}
              placeholder="Kirjeldus..."
              minRows={3}
            />
          )}
        </div>
      ))}
      <button
        onClick={() => setSpecs([...specs, { title: 'UUS BLOKK', description: '' }])}
        className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-[#4FC1E9] transition"
      >
        <Plus className="w-4 h-4" /> Lisa spetsifikatsiooni blokk
      </button>
    </div>
  );
}

// ─── Images Editor ────────────────────────────────────────────────────────────
function ImagesEditor({ images, onChange }: { images: string[]; onChange: (imgs: string[]) => void }) {
  const [newUrl, setNewUrl] = useState('');

  const move = (i: number, dir: 'up' | 'down') => {
    const next = [...images];
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const add = () => {
    if (!newUrl.trim()) return;
    onChange([...images, newUrl.trim()]);
    setNewUrl('');
  };

  return (
    <div className="space-y-2">
      {images.map((url, i) => (
        <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-xl p-2 border border-gray-200">
          <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg flex-shrink-0 bg-white border border-gray-100" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 truncate">{url}</p>
            <span className="text-xs text-gray-400">Pilt {i + 1}</span>
          </div>
          <div className="flex flex-col gap-1">
            <button onClick={() => move(i, 'up')} disabled={i === 0} className="text-gray-400 hover:text-gray-900 disabled:opacity-20 transition">
              <ChevronUp className="w-4 h-4" />
            </button>
            <button onClick={() => move(i, 'down')} disabled={i === images.length - 1} className="text-gray-400 hover:text-gray-900 disabled:opacity-20 transition">
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
          <button onClick={() => onChange(images.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 transition ml-1">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <input
          type="text"
          value={newUrl}
          onChange={e => setNewUrl(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && add()}
          placeholder="Lisa pildi URL..."
          className="flex-1 bg-white border border-gray-300 text-gray-900 text-sm px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4FC1E9]"
        />
        <button
          onClick={add}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-xl border border-gray-300 transition"
        >
          Lisa
        </button>
      </div>
    </div>
  );
}

// ─── Description Editor ───────────────────────────────────────────────────────
function DescriptionEditor({ lines, onChange }: { lines: string[]; onChange: (l: string[]) => void }) {
  const update = (i: number, val: string) => onChange(lines.map((l, j) => (j === i ? val : l)));
  const remove = (i: number) => onChange(lines.filter((_, j) => j !== i));
  return (
    <div className="space-y-2">
      {lines.map((line, i) => (
        <div key={i} className="flex gap-2 items-start">
          <AutoTextarea
            value={line}
            onChange={v => update(i, v)}
            placeholder={`Lõik ${i + 1}...`}
            minRows={2}
          />
          <button onClick={() => remove(i)} className="mt-1 text-gray-400 hover:text-red-500 transition flex-shrink-0">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange([...lines, ''])}
        className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-[#4FC1E9] transition"
      >
        <Plus className="w-4 h-4" /> Lisa lõik
      </button>
    </div>
  );
}

// ─── Field helpers ────────────────────────────────────────────────────────────
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full bg-white border border-gray-300 text-gray-900 text-sm px-3 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent transition";

function TextInput({ value, onChange, placeholder }: {
  value: string | number; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={inputCls}
    />
  );
}

function NumInput({ value, onChange, placeholder }: {
  value: number | null | undefined; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <input
      type="number"
      step="0.01"
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={inputCls}
    />
  );
}

// ─── Main Drawer ──────────────────────────────────────────────────────────────
export default function ProductEditDrawer({ product, onClose, onSaved }: Props) {
  const [activeTab, setActiveTab] = useState<'product' | number>('product');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [brand, setBrand] = useState(product.brand || '');
  const [productClass, setProductClass] = useState(product.class || '');
  const [category, setCategory] = useState<string[]>(product.category || []);
  const [tags, setTags] = useState<string[]>(product.tags || []);
  const [video, setVideo] = useState(product.video || '');
  const [specs, setSpecs] = useState<Spec[]>(Array.isArray(product.specifications) ? product.specifications : []);

  const [variants, setVariants] = useState<any[]>(
    (product.variants || []).map((v: any) => ({
      ...v,
      images: [...(v.images || [])],
      description: [...(v.description || [])],
    }))
  );

  const updateVariant = (i: number, field: string, value: any) =>
    setVariants(prev => prev.map((v, idx) => (idx === i ? { ...v, [field]: value } : v)));

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      const { error: productError } = await supabase
        .from('products')
        .update({ brand, class: productClass, category, tags, video: video || null, specifications: specs.length > 0 ? specs : null })
        .eq('id', product.id);
      if (productError) throw productError;

      for (const v of variants) {
        const { error: variantError } = await supabase
          .from('product_variants')
          .update({
            name: v.name,
            color: v.color,
            color_code: v.color_code,
            price: parseFloat(v.price) || 0,
            discounted_price: v.discounted_price ? parseFloat(v.discounted_price) : null,
            source_price: parseFloat(v.source_price) || 0,
            stock: parseInt(v.stock) || 0,
            new: v.new || false,
            review_count: parseInt(v.review_count) || 0,
            images: v.images,
            description: v.description,
          })
          .eq('id', v.id);
        if (variantError) throw variantError;
      }

      invalidateProductsCache();
      setSaveSuccess(true);
      setTimeout(() => onSaved(), 900);
    } catch (err: any) {
      setSaveError(err.message || "Salvestamine ebaõnnestus. Kontrolli RLS reegleid Supabase'is.");
    } finally {
      setSaving(false);
    }
  };

  const variantIdx = typeof activeTab === 'number' ? activeTab : -1;
  const activeVariant = variantIdx >= 0 ? variants[variantIdx] : null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer panel — white theme */}
      <div className="w-full max-w-2xl bg-white flex flex-col shadow-2xl border-l border-gray-200 overflow-hidden">

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-gray-900 font-bold text-lg truncate">{brand || product.brand}</h2>
            <p className="text-gray-500 text-sm">
              {PRODUCT_CLASSES.find(c => c.id === productClass)?.name || productClass} · {variants.length} varianti
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 px-4 pt-3 border-b border-gray-200 overflow-x-auto flex-shrink-0 bg-white">
          <button
            onClick={() => setActiveTab('product')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors ${
              activeTab === 'product'
                ? 'bg-gray-100 text-gray-900 border border-b-0 border-gray-200'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Toote info
          </button>
          {variants.map((v, i) => (
            <button
              key={v.id}
              onClick={() => setActiveTab(i)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors flex items-center gap-2 ${
                activeTab === i
                  ? 'bg-gray-100 text-gray-900 border border-b-0 border-gray-200'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              <div
                className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0"
                style={{ backgroundColor: v.color_code || '#888' }}
              />
              <span className="max-w-[100px] truncate">{v.name || `Variant ${i + 1}`}</span>
            </button>
          ))}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6 bg-white">

          {/* ── PRODUCT TAB ── */}
          {activeTab === 'product' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Bränd">
                  <TextInput value={brand} onChange={setBrand} placeholder="nt Blunt" />
                </Field>
                <Field label="Toote tüüp">
                  <select
                    value={productClass}
                    onChange={e => setProductClass(e.target.value)}
                    className={inputCls}
                  >
                    {PRODUCT_CLASSES.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <ChipInput label="Kategooriad (Enter lisab)" chips={category} setChips={setCategory} />
              <ChipInput label="Sildid (Enter lisab)" chips={tags} setChips={setTags} />

              <Field label="Video URL (valikuline)">
                <TextInput value={video} onChange={setVideo} placeholder="https://youtube.com/..." />
              </Field>

              <div>
                <p className="text-xs font-medium text-gray-600 mb-3">Spetsifikatsioonid</p>
                <SpecsEditor specs={specs} setSpecs={setSpecs} />
              </div>
            </div>
          )}

          {/* ── VARIANT TAB ── */}
          {activeVariant && (
            <div className="space-y-6">

              {/* Basic info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Variandi andmed</h3>
                <div className="space-y-4">
                  <Field label="Nimi">
                    <TextInput
                      value={activeVariant.name}
                      onChange={v => updateVariant(variantIdx, 'name', v)}
                      placeholder="nt Blunt Complete — Must/Black"
                    />
                  </Field>

                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Värv (tekst)">
                      <TextInput
                        value={activeVariant.color}
                        onChange={v => updateVariant(variantIdx, 'color', v)}
                        placeholder="nt Must/Black"
                      />
                    </Field>
                    <Field label="Värvikood (hex)">
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={activeVariant.color_code || '#000000'}
                          onChange={e => updateVariant(variantIdx, 'color_code', e.target.value)}
                          className="w-12 h-10 rounded-lg border border-gray-300 bg-white cursor-pointer flex-shrink-0"
                        />
                        <TextInput
                          value={activeVariant.color_code}
                          onChange={v => updateVariant(variantIdx, 'color_code', v)}
                          placeholder="#000000"
                        />
                      </div>
                    </Field>
                  </div>
                </div>
              </div>

              {/* Pricing & stock */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Hind ja laoseis</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Müügihind (€)">
                    <NumInput value={activeVariant.price} onChange={v => updateVariant(variantIdx, 'price', v)} placeholder="0.00" />
                  </Field>
                  <Field label="Soodustushind (€, tühi = pole)">
                    <NumInput value={activeVariant.discounted_price} onChange={v => updateVariant(variantIdx, 'discounted_price', v || null)} placeholder="Pole soodustust" />
                  </Field>
                  <Field label="Hanke/ostuhind (€)">
                    <NumInput value={activeVariant.source_price} onChange={v => updateVariant(variantIdx, 'source_price', v)} placeholder="0.00" />
                  </Field>
                  <Field label="Laoseis (tk)">
                    <NumInput value={activeVariant.stock} onChange={v => updateVariant(variantIdx, 'stock', v)} placeholder="0" />
                  </Field>
                  <Field label="Arvustuste arv">
                    <NumInput value={activeVariant.review_count} onChange={v => updateVariant(variantIdx, 'review_count', v)} placeholder="0" />
                  </Field>
                  <Field label="Uus toode">
                    <button
                      onClick={() => updateVariant(variantIdx, 'new', !activeVariant.new)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors border ${
                        activeVariant.new
                          ? 'bg-green-50 border-green-400 text-green-700'
                          : 'bg-gray-50 border-gray-300 text-gray-500'
                      }`}
                    >
                      {activeVariant.new && <Check className="w-4 h-4" />}
                      {activeVariant.new ? 'Uus toode' : 'Märgi uueks'}
                    </button>
                  </Field>
                </div>
              </div>

              {/* Images */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Pildid ({activeVariant.images?.length || 0})</h3>
                <ImagesEditor
                  images={activeVariant.images || []}
                  onChange={imgs => updateVariant(variantIdx, 'images', imgs)}
                />
              </div>

              {/* Description */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Kirjeldus ({activeVariant.description?.length || 0} lõiku)</h3>
                <DescriptionEditor
                  lines={activeVariant.description || []}
                  onChange={lines => updateVariant(variantIdx, 'description', lines)}
                />
              </div>

            </div>
          )}
        </div>

        {/* Footer / Save bar */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center gap-3 flex-shrink-0">
          {saveSuccess && (
            <span className="text-green-600 text-sm flex items-center gap-1">
              <Check className="w-4 h-4" /> Salvestatud!
            </span>
          )}
          {saveError && <span className="text-red-500 text-sm flex-1">{saveError}</span>}
          <div className="ml-auto flex gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm text-gray-500 hover:text-gray-900 border border-gray-300 hover:border-gray-400 rounded-xl transition"
            >
              Tühista
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 text-sm font-bold bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white rounded-xl transition disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Salvestab...' : 'Salvesta'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
